import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Toast from '../../../components/ui/Toast';
import api from '../../../utils/api';
import { getImageUrl } from '../../../utils/config';
import CategoryCard from '../components/CategoryCard';

const StoreCategoriesPage = () => {
  const { slug } = useParams();
  const [categories, setCategories] = useState([]);
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  useEffect(() => {
    fetchStoreAndCategories();
  }, [slug]);

  const fetchStoreAndCategories = async () => {
    try {
      setLoading(true);
      // Fetch the store data using the working endpoint
      const response = await api.get(`/stores/${slug}`);
      
      if (response.data.success) {
        const storeData = response.data.data;
        setStore(storeData);
        // Use the categories from the store data
        setCategories(storeData.categories || []);
      } else {
        showToast(response.data.message || 'Failed to load store', 'error');
      }
    } catch (error) {
      console.error('Error fetching store:', error);
      const errorMessage = error.response?.data?.message || 'Failed to load store';
      showToast(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
  };

  const closeToast = () => {
    setToast({ show: false, message: '', type: 'success' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading categories...</p>
        </div>
      </div>
    );
  }

  if (!store) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Store Not Found</h2>
          <p className="text-gray-600 mb-6">The store you're looking for doesn't exist.</p>
          <Link
            to="/"
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <div className="flex items-center space-x-4">
              {/* Store Icon */}
              {store.icon && (
                <img
                  src={getImageUrl(store.icon)}
                  alt={`${store.name} icon`}
                  className="w-12 h-12 rounded-lg object-cover border border-gray-200"
                  onError={e => {
                    e.target.onerror = null;
                    e.target.style.display = 'none';
                  }}
                />
              )}
              
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {store.name}
                </h1>
                <p className="text-gray-600">Browse our categories</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <nav className="mb-8">
          <ol className="flex items-center space-x-2 text-sm text-gray-500">
            <li>
              <Link to="/" className="hover:text-gray-700">
                Home
              </Link>
            </li>
            <li>
              <span className="mx-2">/</span>
            </li>
            <li>
              <Link to={`/stores/${slug}`} className="hover:text-gray-700">
                {store.name}
              </Link>
            </li>
            <li>
              <span className="mx-2">/</span>
            </li>
            <li className="text-gray-900">Categories</li>
          </ol>
        </nav>

        {/* Categories Grid */}
        {categories.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-400 text-6xl mb-4">📁</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No categories found</h3>
            <p className="text-gray-500">
              This store hasn't added any categories yet.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {categories.map((category) => (
              <CategoryCard
                key={category.id}
                category={category}
                storeSlug={slug}
              />
            ))}
          </div>
        )}
      </div>

      {/* Toast */}
      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.show}
        onClose={closeToast}
      />
    </div>
  );
};

export default StoreCategoriesPage; 